import { z } from 'zod';
import { UserRoleSchema } from './user';

/**
 * Zod Schema für AuditLog — Runtime-Validierung + Type-Inference.
 * Admin-Aktionen (append-only). changes ist ein JSON-String mit Änderungen.
 *
 * @example action: "ticket.assign", "event.create", "user.block"
 * @example entity: "ticket", "event", "user"
 */
export const AuditLogSchema = z.object({
  id: z.string().cuid(),
  userId: z.string(),
  action: z.string(), // z.B. "ticket.assign", "event.create", "user.block"
  entity: z.string(), // z.B. "ticket", "event", "user"
  entityId: z.string(),
  changes: z.string().nullable().optional(), // JSON-String mit Änderungen
  ipAddress: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type AuditLog = z.infer<typeof AuditLogSchema>;

/**
 * Schema für AuditLog-Erstellung.
 * AuditLogs sind append-only — kein Update-Schema nötig.
 */
export const CreateAuditLogSchema = AuditLogSchema.pick({
  userId: true,
  action: true,
  entity: true,
  entityId: true,
  changes: true,
  ipAddress: true,
});

/** TypeScript Type für AuditLog-Erstellung */
export type CreateAuditLog = z.infer<typeof CreateAuditLogSchema>;

/**
 * AuditLog mit aufgelöstem Benutzer-Anzeigenamen.
 * Wird für die paginierten Audit-Log-Listen im Dashboard genutzt.
 */
export const AuditLogWithUserSchema = AuditLogSchema.extend({
  /** Anzeigename des Users der die Aktion ausgeführt hat, oder null wenn nicht auflösbar */
  userDisplayName: z.string().nullable(),
});

/** TypeScript Type für AuditLog mit User-Anzeigenamen */
export type AuditLogWithUser = z.infer<typeof AuditLogWithUserSchema>;

/**
 * Schema für paginierte Audit-Log-Listenresponse.
 * Entspricht dem Response von GET /audit-logs.
 */
export const AuditLogListResponseSchema = z.object({
  data: z.array(AuditLogWithUserSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

/** TypeScript Type für paginierte Audit-Log-Response */
export type AuditLogListResponse = z.infer<typeof AuditLogListResponseSchema>;

/**
 * Schema für den Request-Body von PATCH /persons/:id/role.
 * Erlaubt nur das Feld `role` mit einem gültigen UserRole-Wert.
 *
 * @example
 * ```json
 * { "role": "team" }
 * ```
 */
export const ChangeRoleSchema = z.object({
  role: UserRoleSchema,
});

/** TypeScript Type für Rollen-Änderungs-Body */
export type ChangeRole = z.infer<typeof ChangeRoleSchema>;
